/**
 * 연동 키 원문 [원문 복사]·[원문 등록] — 복사 흐름과 등록 창.
 *
 * @spec:2026-10-15-phai-admin-연동키-원문-복사 — 「구현 계약 › 프론트 계약」, D2·D10·D13
 *
 * 원문은 평소 화면에 렌더하지 않는다. [원문 복사]는 reveal로 받은 원문을 클립보드에만 넣고, 클립보드가
 * 막힌 환경에서만 공용 원문 표시 창(showSecretReveal)으로 대신 보여준다(거기서 직접 선택·복사할 수 있다).
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import { ConfirmManager, ToastManager } from "components/organisms/CommonUI";
import { PartnerAdminError } from "api/partnerAdminService";
import { validateRawKeyInput } from "utils/partnerAdmin";
import { showSecretReveal } from "./SecretRevealModal";
import { ErrorMsg, Field, FieldLabel, Hint, ModalBody, ModalFooter, ModalTitle, Notice, TextInput } from "./style";

export const REVEAL_COPY_DONE = "연동 키 원문을 복사했습니다";
export const REL_REVEAL_CONFIRM = "운영(rel) 연동 키 원문을 클립보드에 복사합니다. 복사 기록이 남습니다";
/** 클립보드가 막혀 원문 창으로 대체할 때의 안내(발급 창 「한 번만 보임」과 다르다) */
export const REVEAL_ALT_NOTICE = "클립보드에 넣지 못해 원문을 보여 드립니다. 직접 복사하세요";
export const RAW_REGISTER_GUIDE =
  "이 키의 원문을 붙여 넣으세요. 지문이 맞아야 저장됩니다. 키 값은 바뀌지 않습니다";

/** 복호화 실패·불일치 — 그 키 행에 [원문 등록] 보조 버튼을 여는 code(D10·D9) */
const RECOVERABLE_RAW_CODES = ["RAW_KEY_DECRYPT_FAILED", "RAW_KEY_MISMATCH"];
export const isRecoverableRawError = (err: unknown): boolean =>
  err instanceof PartnerAdminError && !!err.bodyCode && RECOVERABLE_RAW_CODES.includes(err.bodyCode);

interface RevealOrchestration {
  onRecoverable?: () => void;
  onSettled?: () => void;
}

/** ClipboardItem 경로가 이 환경에서 쓸 수 있는가(Safari 등). jsdom·구형은 writeText로 내려간다. */
const canUseClipboardItem = (): boolean =>
  typeof ClipboardItem !== "undefined" && typeof navigator !== "undefined" && !!navigator.clipboard?.write;

/**
 * reveal로 원문을 받아 클립보드에 쓴다. 순서(계약):
 *  1) ClipboardItem(text/plain = reveal 결과 Blob)을 **동기적으로** 시작한다 — Safari의 사용자 제스처를 잃지 않게.
 *  2) ClipboardItem이 없거나 실패하면 reveal 결과로 writeText.
 *  3) 그것도 실패하면 원문 표시 창(대체 경로, 안내 문구가 다르다).
 * reveal 자체가 실패하면(409 등) 조용히 멈춘다 — 사유는 훅(onError)이 토스트로 보여준다. 복구 가능한
 * 오류(복호화·불일치)면 onRecoverable로 알려 그 행에 [원문 등록]을 열게 한다.
 */
export const copyRevealedKey = async (
  reveal: () => Promise<{ rawKey: string }>,
  opts: RevealOrchestration = {}
): Promise<void> => {
  const revealPromise = reveal();
  // 1) ClipboardItem 경로를 동기적으로 시작한다(첫 await 전).
  let itemWrite: Promise<void> | null = null;
  if (canUseClipboardItem()) {
    try {
      const item = new ClipboardItem({
        "text/plain": revealPromise.then((r) => new Blob([r.rawKey], { type: "text/plain" })),
      });
      itemWrite = navigator.clipboard.write([item]);
    } catch {
      itemWrite = null;
    }
  }

  try {
    if (itemWrite) {
      try {
        await itemWrite;
        await revealPromise; // 성공 확정(토스트 전 reveal 성공 보장)
        ToastManager.show({ content: REVEAL_COPY_DONE, duration: 2000 });
        return;
      } catch {
        // ClipboardItem 실패 — reveal 실패인지 클립보드 거부인지 아래에서 가린다.
      }
    }

    let rawKey: string;
    try {
      ({ rawKey } = await revealPromise);
    } catch (err) {
      if (isRecoverableRawError(err)) opts.onRecoverable?.();
      return;
    }
    try {
      await navigator.clipboard.writeText(rawKey);
      ToastManager.show({ content: REVEAL_COPY_DONE, duration: 2000 });
    } catch {
      showSecretReveal({ title: "연동 키 원문", raw: rawKey, provision: null, notice: REVEAL_ALT_NOTICE });
    }
  } finally {
    opts.onSettled?.();
  }
};

/**
 * [원문 복사] 요청. 운영(REL)이면 확인 창을 먼저 거친다(D2 완화책 — 복사마다 이력이 남음).
 * 확인 창은 [복사]를 누르는 즉시 닫고 복사를 시작한다 — 복사 과정에서 클립보드가 막혀 원문 표시 창을
 * 열 때 확인 창과 모달 자리를 다투지 않게 하기 위해서다(둘 다 ConfirmManager 한 자리를 쓴다).
 */
export const requestRevealCopy = (opts: {
  isRel: boolean;
  reveal: () => Promise<{ rawKey: string }>;
  onStart?: () => void;
  onSettled?: () => void;
  onRecoverable?: () => void;
}): void => {
  const run = () => {
    opts.onStart?.();
    void copyRevealedKey(opts.reveal, { onRecoverable: opts.onRecoverable, onSettled: opts.onSettled });
  };
  if (!opts.isRel) {
    run();
    return;
  }
  ConfirmManager.show({
    title: "연동 키 원문 복사",
    body: (
      <Notice $tone="warn" data-testid="raw-reveal-confirm-text">
        {REL_REVEAL_CONFIRM}
      </Notice>
    ),
    buttons: [
      <Button
        key="cancel"
        type="Text"
        size="Medium"
        variant="Ghost"
        color="Secondary"
        onClick={() => ConfirmManager.hide()}
      >
        취소
      </Button>,
      <Button
        key="ok"
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        onClick={() => {
          ConfirmManager.hide();
          run();
        }}
        data-testid="raw-reveal-confirm-ok"
      >
        복사
      </Button>,
    ],
    showBackdrop: true,
    backdropClose: true,
    showCloseButton: false,
  });
};

// ---------------------------------------------------------------------------
// [원문 등록] — 원문이 없는 기존 키에 원문을 넣는다(비밀번호형 입력). 지문 일치는 서버가 판정한다.
// ---------------------------------------------------------------------------

interface RawKeyRegisterModalProps {
  /** 어떤 키의 원문인지 짚어 주는 지문(key_hash 앞 8자) */
  fingerprint: string;
  onSubmit: (rawKey: string) => Promise<void>;
  onCancel: () => void;
}

export const RawKeyRegisterModal: React.FC<RawKeyRegisterModalProps> = ({ fingerprint, onSubmit, onCancel }) => {
  const [rawKey, setRawKey] = useState("");
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const check = validateRawKeyInput(rawKey);
    setError(check.ok ? undefined : check.message);
    if (!check.ok) return;
    setSubmitting(true);
    try {
      await onSubmit(rawKey);
    } catch {
      // 실패 사유(지문 불일치 등)는 훅이 서버 message 그대로 토스트로 보여준다. 창은 남겨 둔다.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBody data-testid="raw-key-register">
      <ModalTitle>연동 키 원문 등록</ModalTitle>
      <Notice $tone="info">{RAW_REGISTER_GUIDE}</Notice>
      <Field>
        <FieldLabel>원문 (지문 {fingerprint})</FieldLabel>
        <TextInput
          type="password"
          autoComplete="new-password"
          value={rawKey}
          onChange={(e) => setRawKey(e.target.value)}
          data-testid="raw-key-register-value"
        />
        <Hint>붙여 넣은 값의 지문이 저장된 키와 같을 때만 저장됩니다. 키 값 자체는 바뀌지 않습니다.</Hint>
        {error && <ErrorMsg data-testid="raw-key-register-error">{error}</ErrorMsg>}
      </Field>
      <ModalFooter>
        <Button type="Text" size="Medium" variant="Ghost" color="Secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="raw-key-register-submit"
        >
          등록
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};
